import { zodResolver } from "@hookform/resolvers/zod";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { type KeyboardEvent, useCallback } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { formatToSSN } from "../utils/formatToSSN";

const SSN_PATTERN = /^(?!666|000|9\d{2})\d{3}-(?!00)\d{2}-(?!0{4})\d{4}$/;

const ssnFormSchema = z
  .object({
    ssn: z.string().regex(SSN_PATTERN, "SSN is invalid"),
    confirmSsn: z.string(),
  })
  .refine((data) => data.confirmSsn === data.ssn, {
    message: "SSN does not match",
    path: ["confirmSsn"],
  });

export type SsnFormValues = z.infer<typeof ssnFormSchema>;

export function useAddSsn() {
  const worker = useDefinedWorker();

  const {
    control,
    setValue,
    trigger,
    clearErrors,
    reset,
    handleSubmit,
    formState: { errors },
  } = useForm<SsnFormValues>({
    resolver: zodResolver(ssnFormSchema),
    defaultValues: {
      ssn: worker?.socialSecurityNumber ? `xxx-xx-${worker.socialSecurityNumber}` : "",
      confirmSsn: "",
    },
  });

  function handleChangeSsn(field: keyof SsnFormValues) {
    return (event: React.ChangeEvent<HTMLInputElement>) => {
      const formattedValue = event.target.value?.toString().replace(/[^\dx-]/g, "");
      setValue(field, formattedValue, { shouldValidate: false });
      clearErrors(field);
    };
  }

  function handleKeyUpSsn(field: keyof SsnFormValues) {
    return (event: KeyboardEvent<HTMLInputElement>) => {
      const { value } = event.currentTarget;

      if (event.key !== "Backspace" && event.key !== "Delete") {
        setValue(field, formatToSSN(value), { shouldValidate: false });
        return;
      }

      setValue(field, value, { shouldValidate: false });
    };
  }

  function handleKeyPressSSN(event: KeyboardEvent<HTMLInputElement>) {
    if (Number.isNaN(Number(event.key))) {
      event.preventDefault();
    }
  }

  async function handleBlurSSN() {
    await trigger("ssn");
  }

  async function handleBlurConfirmSSN() {
    await trigger("confirmSsn");
  }

  async function handleSaveSSN(
    onSave: (ssnNumber: string) => Promise<void>,
    onValidationError?: (fieldErrors: { ssn?: string; confirmSsn?: string }) => void
  ) {
    await handleSubmit(
      async (data) => {
        const ssnNumber = data.ssn.replace(/\D/g, "");
        await onSave(ssnNumber);
      },
      (fieldErrors) => {
        onValidationError?.({
          ssn: fieldErrors.ssn?.message,
          confirmSsn: fieldErrors.confirmSsn?.message,
        });
      }
    )();
  }

  const resetForm = useCallback(() => {
    reset({
      ssn: worker?.socialSecurityNumber ? `xxx-xx-${worker.socialSecurityNumber}` : "",
      confirmSsn: "",
    });
  }, [reset, worker?.socialSecurityNumber]);

  return {
    control,
    errors,
    handleChangeSSN: handleChangeSsn("ssn"),
    handleChangeConfirmSSN: handleChangeSsn("confirmSsn"),
    handleKeyUpSSN: handleKeyUpSsn("ssn"),
    handleKeyUpConfirmSSN: handleKeyUpSsn("confirmSsn"),
    handleBlurSSN,
    handleBlurConfirmSSN,
    handleKeyPressSSN,
    handleSaveSSN,
    resetForm,
  };
}
